import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import api from '../../../../api/client';
import { AuthContext } from '../../../../App';
import Logo from '../../../../assets/AptiviewLogo.png';
import { FiUserPlus, FiCheckCircle } from 'react-icons/fi';
import ReCaptcha from '../../../common/ReCaptcha';
import CustomToastContainer from '../../../CustomToastContainer';
import AIInterviewTheme from './AIInterviewTheme';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';

export default function SignUp({ onLogin }) {
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        minLength: false,
        hasUppercase: false,
        hasSpecialChar: false,
        hasNumber: false
    });
    const { login } = useContext(AuthContext);
    const currentYear = new Date().getFullYear();
    // Password strength validation function
    const validatePassword = (pwd) => {
        const minLength = pwd.length >= 12;
        const hasUppercase = /[A-Z]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);

        setPasswordStrength({
            minLength,
            hasUppercase,
            hasSpecialChar,
            hasNumber
        });

        return minLength && hasUppercase && hasSpecialChar && hasNumber;
    };

    const isPasswordValid = () => {
        return passwordStrength.minLength &&
            passwordStrength.hasUppercase &&
            passwordStrength.hasSpecialChar &&
            passwordStrength.hasNumber;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid()) {
            toast.error('Password does not meet the required strength criteria', {
                icon: "⚠️"
            });
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match', {
                icon: "⚠️"
            });
            return;
        }

        if (!recaptchaToken) {
            toast.error('Please complete the reCAPTCHA verification', {
                icon: "⚠️"
            });
            return;
        }

        setIsLoading(true);

        try {
             await api.post('/auth/register/', {
                firstName: firstName,
                lastName: lastName,
                email,
                password,
                password2: confirmPassword,
                recaptcha_response: recaptchaToken
            });

            setRegistrationComplete(true);
            toast.success('User registered successfully! Please check your email to activate your account.', {
                icon: "🎉"
            });

            // Clear form fields
            setFirstName('');
            setLastName('');
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setRecaptchaToken('');

        } catch (err) {
            const errorMessage = err.response?.data?.detail ||
                err.response?.data?.message ||
                Object.values(err.response?.data || {}).join(', ') ||
                'Signup failed. Please try again.';
            
            toast.error(errorMessage, {
                icon: "❌"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setIsLoading(true);
        try {
            const response = await api.post('auth/google/', {
                credential: credentialResponse.credential,
            });
            if (response.data.requires_consent) {
                localStorage.setItem('requires_consent', 'true');
            } else {
                localStorage.removeItem('requires_consent');
            }
            login(response.data.token, response.data.user);
            toast.success('Google signup successful! Redirecting...', {
                icon: "🎉"
            });
            navigate('/');
        } catch (err) {
            console.error('Google signup error:', err);
            toast.error('Google signup failed. Please try again.', {
                icon: "❌"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error('Google signup failed. Please try again.', {
            icon: "❌"
        });
    };

    // If registration is complete, show success message
    if (registrationComplete) {
        return (
            <div style={{ 
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                display: 'flex',
                padding: 0,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Custom Toast Container */}
                <CustomToastContainer />
                
                {/* Animated Background Elements */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
                    `,
                    pointerEvents: 'none'
                }} />

                <div className="row flex-fill w-100 m-0" style={{ height: '100%' }}>
                    {/* Left Side - AI Interview Theme */}
                    <AIInterviewTheme 
                        title="Welcome to Aptiview"
                        description="Your account has been created successfully! Check your email to activate your account and start exploring our AI-powered interview platform."
                    />

                    {/* Right Side - Success Message */}
                    <div className="col-lg-4 col-12 d-flex align-items-center justify-content-center p-3 p-lg-4" style={{ 
                        minHeight: '100vh',
                        overflowY: 'auto'
                    }}>
                        <div 
                            className="w-100"
                            style={{ 
                                maxWidth: '400px',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: '1.5rem',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                padding: '2rem',
                                margin: 'auto'
                            }}
                        >
                            {/* Logo */}
                            <div className="text-center mb-4">
                                <img
                                    src={Logo}
                                    alt="AptiView Logo"
                                    style={{ 
                                        transform: 'scale(0.8)',
                                        maxWidth: '100%',
                                        height: 'auto'
                                    }}
                                />
                            </div>

                            {/* Success Content */}
                            <div className="text-center">
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem',
                                    border: '2px solid rgba(34, 197, 94, 0.3)'
                                }}>
                                    <FiCheckCircle size={40} style={{ color: '#22c55e' }} />
                                </div>
                                
                                <h1 style={{ 
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: '800', 
                                    fontSize: '1.75rem',
                                    color: '#22c55e',
                                    marginBottom: '1rem'
                                }}>
                                    Registration Successful!
                                </h1>
                                
                                <div style={{
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '12px',
                                    padding: '1rem',
                                    marginBottom: '1.5rem',
                                    color: '#16a34a',
                                    fontWeight: '500'
                                }}>
                                    <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                                        User registered successfully!
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                        Please check your email to activate your account.
                                    </p>
                                </div>

                                <p style={{ 
                                    color: '#6b7280', 
                                    fontSize: '0.9rem',
                                    lineHeight: '1.5',
                                    marginBottom: '1.5rem'
                                }}>
                                    We've sent an activation link to your email address. 
                                    Click the link in the email to activate your account and complete the registration process.
                                </p>

                                {/* Action Buttons */}
                                <div style={{ display: 'grid', gap: '0.75rem' }}>
                                    <Link
                                        to="/login"
                                        style={{
                                            display: 'block',
                                            padding: '0.85rem 1.5rem',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            fontFamily: 'Poppins, sans-serif',
                                            borderRadius: '10px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                                            color: 'white',
                                            textDecoration: 'none',
                                            textAlign: 'center',
                                            transition: 'all 0.15s ease',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    >
                                        <i className="bi bi-box-arrow-in-right me-2"></i>
                                        Go to Sign In
                                    </Link>
                                    
                                    <button
                                        onClick={() => {
                                            setRegistrationComplete(false);
                                        }}
                                        style={{
                                            padding: '0.85rem 1.5rem',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            fontFamily: 'Poppins, sans-serif',
                                            borderRadius: '10px',
                                            border: '2px solid #e5e7eb',
                                            background: 'rgba(255, 255, 255, 0.9)',
                                            color: '#374151',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <i className="bi bi-person-plus me-2"></i>
                                        Register Another Account
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
            display: 'flex',
            padding: 0,
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Custom Toast Container */}
            <CustomToastContainer />
            
            {/* Animated Background Elements */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
                `,
                pointerEvents: 'none'
            }} />

            <div className="row flex-fill w-100 m-0" style={{ height: '100%' }}>
                {/* Left Side - AI Interview Theme */}
                <AIInterviewTheme 
                    title="Join Aptiview"
                    description="Create your account and unlock the power of AI-driven interviews. Experience intelligent assessments, detailed analytics, and streamlined recruitment processes."
                />

                {/* Right Side - Sign Up Form */}
                <div className="col-lg-4 col-12 d-flex align-items-center justify-content-center p-3 p-lg-4" style={{ 
                    minHeight: '100vh',
                    overflowY: 'auto'
                }}>
                    <div 
                        className="w-100"
                        style={{ 
                            maxWidth: '400px',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '1.5rem',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: '2rem',
                            margin: 'auto'
                        }}
                    >
                        {/* Logo and Header */}
                        <div className="text-center mb-4">
                            <div className="mb-3" style={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                    src={Logo}
                                    alt="AptiView Logo"
                                    style={{ 
                                        transform: 'scale(0.8)',
                                        maxWidth: '100%',
                                        height: 'auto'
                                    }}
                                />
                            </div>
                            <h1 
                                style={{ 
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: '800', 
                                    fontSize: '1.75rem',
                                    background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Create Account
                            </h1>
                            <p style={{ 
                                color: '#6b7280', 
                                fontSize: '0.9rem', 
                                fontWeight: '500',
                                margin: 0
                            }}>
                                Sign up with your information
                            </p>
                        </div>

                        {/* Sign Up Form */}
                        <form onSubmit={handleSubmit}>
                            {/* First Name */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label 
                                    htmlFor="first-name"
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <i className="bi bi-person me-2"></i>
                                    First Name
                                </label>
                                <input
                                    id="first-name"
                                    type="text"
                                    placeholder="Enter first name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        fontSize: '0.9rem',
                                        color: '#111827',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        transition: 'all 0.15s ease',
                                        backdropFilter: 'blur(10px)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#0ea5e9';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                                        e.target.style.background = 'white';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = 'none';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                    }}
                                />
                            </div>

                            {/* Last Name */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label 
                                    htmlFor="last-name"
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <i className="bi bi-person me-2"></i>
                                    Last Name
                                </label>
                                <input
                                    id="last-name"
                                    type="text"
                                    placeholder="Enter last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        fontSize: '0.9rem',
                                        color: '#111827',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        transition: 'all 0.15s ease',
                                        backdropFilter: 'blur(10px)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#0ea5e9';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                                        e.target.style.background = 'white';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = 'none';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                    }}
                                />
                            </div>

                            {/* Email */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label 
                                    htmlFor="admin-email"
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <i className="bi bi-envelope me-2"></i>
                                    Email Address
                                </label>
                                <input
                                    id="admin-email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    autoComplete="username"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        fontSize: '0.9rem',
                                        color: '#111827',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        transition: 'all 0.15s ease',
                                        backdropFilter: 'blur(10px)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#0ea5e9';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                                        e.target.style.background = 'white';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = 'none';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                    }}
                                />
                            </div>

                            {/* Password */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label 
                                    htmlFor="admin-password"
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <i className="bi bi-lock me-2"></i>
                                    Password
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        id="admin-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => {
                                            const newPassword = e.target.value;
                                            setPassword(newPassword);
                                            validatePassword(newPassword);
                                        }}
                                        required
                                        disabled={isLoading}
                                        autoComplete="new-password"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            paddingRight: '2.5rem',
                                            fontSize: '0.9rem',
                                            color: '#111827',
                                            background: 'rgba(255, 255, 255, 0.9)',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '10px',
                                            transition: 'all 0.15s ease',
                                            backdropFilter: 'blur(10px)',
                                            fontFamily: 'Inter, sans-serif'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#0ea5e9';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                                            e.target.style.background = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        style={{
                                            position: 'absolute',
                                            right: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: '#6b7280',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            padding: '0.2rem'
                                        }}
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                                    </button>
                                </div>

                                {/* Password Strength Indicator */}
                                {password && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                                            Password Requirements:
                                        </div>
                                        <div style={{ display: 'grid', gap: '0.125rem' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.25rem', 
                                                fontSize: '0.7rem',
                                                color: passwordStrength.minLength ? '#22c55e' : '#ef4444'
                                            }}>
                                                <i className={`bi ${passwordStrength.minLength ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                Minimum 12 characters
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.25rem', 
                                                fontSize: '0.7rem',
                                                color: passwordStrength.hasUppercase ? '#22c55e' : '#ef4444'
                                            }}>
                                                <i className={`bi ${passwordStrength.hasUppercase ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                One uppercase letter
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.25rem', 
                                                fontSize: '0.7rem',
                                                color: passwordStrength.hasSpecialChar ? '#22c55e' : '#ef4444'
                                            }}>
                                                <i className={`bi ${passwordStrength.hasSpecialChar ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                One special character
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.25rem', 
                                                fontSize: '0.7rem',
                                                color: passwordStrength.hasNumber ? '#22c55e' : '#ef4444'
                                            }}>
                                                <i className={`bi ${passwordStrength.hasNumber ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                One number
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <div style={{ 
                                                width: '100%',
                                                height: '4px',
                                                background: '#e5e7eb',
                                                borderRadius: '2px',
                                                overflow: 'hidden'
                                            }}>
                                                <div
                                                    style={{
                                                        width: `${(Object.values(passwordStrength).filter(Boolean).length / 4) * 100}%`,
                                                        height: '100%',
                                                        background: isPasswordValid() ? '#22c55e' : '#f59e0b',
                                                        transition: 'width 0.3s ease'
                                                    }}
                                                />
                                            </div>
                                            <div style={{ 
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                color: isPasswordValid() ? '#22c55e' : '#f59e0b',
                                                marginTop: '0.25rem'
                                            }}>
                                                {isPasswordValid() ? 'Strong Password ✓' : `${Object.values(passwordStrength).filter(Boolean).length}/4 Requirements Met`}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label 
                                    htmlFor="confirmPassword"
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <i className="bi bi-lock-fill me-2"></i>
                                    Confirm Password
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                        autoComplete="new-password"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            paddingRight: '2.5rem',
                                            fontSize: '0.9rem',
                                            color: '#111827',
                                            background: 'rgba(255, 255, 255, 0.9)',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '10px',
                                            transition: 'all 0.15s ease',
                                            backdropFilter: 'blur(10px)',
                                            fontFamily: 'Inter, sans-serif'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#0ea5e9';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                                            e.target.style.background = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        style={{
                                            position: 'absolute',
                                            right: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: '#6b7280',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            padding: '0.2rem'
                                        }}
                                    >
                                        <i className={`bi ${showConfirmPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                                    </button>
                                </div>
                                
                                {/* Password Match Indicator */}
                                {confirmPassword && (
                                    <div style={{ 
                                        marginTop: '0.5rem',
                                        fontSize: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        color: password === confirmPassword ? '#22c55e' : '#ef4444'
                                    }}>
                                        <i className={`bi ${password === confirmPassword ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                        {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                                    </div>
                                )}
                            </div>

                            {/* reCAPTCHA */}
                            <div style={{ marginBottom: '1rem' }}>
                                <ReCaptcha
                                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                    onVerify={setRecaptchaToken}
                                    theme="light"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !isPasswordValid() || password !== confirmPassword}
                                style={{ 
                                    width: '100%',
                                    padding: '0.85rem 1.5rem',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    fontFamily: 'Poppins, sans-serif',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: (isLoading || !isPasswordValid() || password !== confirmPassword) 
                                        ? '#9ca3af' 
                                        : 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                                    color: 'white',
                                    cursor: (isLoading || !isPasswordValid() || password !== confirmPassword) 
                                        ? 'not-allowed' 
                                        : 'pointer',
                                    transition: 'all 0.15s ease',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    marginTop: '0.5rem',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading && isPasswordValid() && password === confirmPassword) {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 8px 12px -3px rgba(0, 0, 0, 0.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isLoading && isPasswordValid() && password === confirmPassword) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                    }
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <i className="bi bi-arrow-clockwise me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                                        Creating Account...
                                    </>
                                ) : (
                                    <>
                                        <FiUserPlus style={{ marginRight: '0.5rem' }} />
                                        Create Account
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Sign In Link */}
                        <div className="text-center mt-3 mb-3">
                            <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>
                                Already have an account?{' '}
                                <Link 
                                    to="/login" 
                                    style={{ 
                                        color: '#0ea5e9',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#0369a1'}
                                    onMouseLeave={(e) => e.target.style.color = '#0ea5e9'}
                                >
                                    Sign In
                                </Link>
                            </p>
                        </div>

                        {/* Divider */}
                        <div style={{ display: 'flex', alignItems: 'center', margin: '1rem 0' }}>
                            <hr style={{ 
                                flex: 1, 
                                border: 'none', 
                                height: '1px', 
                                background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)' 
                            }} />
                            <span style={{ 
                                padding: '0 0.75rem', 
                                color: '#6b7280', 
                                fontWeight: '500', 
                                fontSize: '0.8rem' 
                            }}>
                                OR
                            </span>
                            <hr style={{ 
                                flex: 1, 
                                border: 'none', 
                                height: '1px', 
                                background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)' 
                            }} />
                        </div>

                        {/* Google Login */}
                        <div className="text-center">
                            <div 
                                style={{
                                    display: 'inline-block',
                                    padding: '0.3rem',
                                    borderRadius: '10px',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                    useOneTap
                                    theme="outline"
                                    size="medium"
                                    text="signup_with"
                                    shape="rectangular"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center mt-3">
                            <p style={{ color: '#9ca3af', fontSize: '0.7rem', margin: 0 }}>
                                 © {currentYear} AptiView. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @keyframes fadeInScale {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
                
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }

                /* Mobile responsive styles */
                @media (max-width: 991.98px) {
                    .col-12 {
                        padding: 1rem !important;
                    }
                }

                @media (max-width: 575.98px) {
                    .col-12 {
                        padding: 0.75rem !important;
                    }
                }

                /* Ensure no horizontal scroll */
                body {
                    overflow-x: hidden;
                }

                /* Custom scrollbar for webkit browsers */
                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                }

                ::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
            `}
            </style>
        </div>
    );
}